/**
 * Banner Slider — horizontal slide transition, thumbnails, prev/next, progress bar
 */
if (!customElements.get('csk-banner')) {
  class CskBanner extends HTMLElement {
    connectedCallback() {
      this._slides = this.querySelectorAll('[data-csk-slide]');
      this._track = this.querySelector('[data-csk-banner-track]');
      this._thumbs = this.querySelectorAll('[data-csk-thumb]');
      this._prevBtn = this.querySelector('[data-csk-banner-prev]');
      this._nextBtn = this.querySelector('[data-csk-banner-next]');
      this._progressBar = this.querySelector('[data-csk-banner-progress-bar]');

      this._slideCount = this._slides.length;
      if (this._slideCount === 0) return;

      this._autoplay = this.dataset.autoplay === 'true';
      this._interval = (parseInt(this.dataset.interval) || 6) * 1000;
      this._current = 0;
      this._isPaused = false;
      this._timeout = null;

      this._bindEvents();

      if (this._autoplay) {
        this._observeVisibility();
      }
    }

    disconnectedCallback() {
      this._clearTimeout();
      if (this._observer) this._observer.disconnect();
    }

    _bindEvents() {
      /* Prev/next/thumb : navigate and restart autoplay without permanent pause */
      this._prevBtn?.addEventListener('click', () => {
        this._prev();
        if (this._autoplay) this._startAutoplay();
      });

      this._nextBtn?.addEventListener('click', () => {
        this._next();
        if (this._autoplay) this._startAutoplay();
      });

      this._thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          this._goTo(i);
          if (this._autoplay) this._startAutoplay();
        });
      });

      /* Pause on hover (desktop) — only affects hover, not click interactions */
      this._hoverPaused = false;

      this.addEventListener('mouseenter', () => {
        if (this._autoplay) {
          this._hoverPaused = true;
          this._clearTimeout();
          this._stopProgressBar();
        }
      });

      this.addEventListener('mouseleave', () => {
        if (this._autoplay && this._hoverPaused) {
          this._hoverPaused = false;
          this._startAutoplay();
        }
      });

      /* Touch swipe support */
      let startX = 0;
      let diff = 0;
      const viewport = this.querySelector('[data-csk-banner-viewport]');
      if (viewport) {
        viewport.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          diff = 0;
        }, { passive: true });

        viewport.addEventListener('touchmove', (e) => {
          diff = e.touches[0].clientX - startX;
        }, { passive: true });

        viewport.addEventListener('touchend', () => {
          if (Math.abs(diff) > 50) {
            if (diff < 0) {
              this._next();
            } else {
              this._prev();
            }
            if (this._autoplay) this._startAutoplay();
          }
        });
      }
    }

    _observeVisibility() {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this._hoverPaused) {
              this._startAutoplay();
            } else {
              this._clearTimeout();
              this._stopProgressBar();
            }
          });
        },
        { threshold: 0.3 }
      );
      this._observer.observe(this);
    }

    _goTo(index) {
      if (index === this._current) return;
      this._current = index;

      /* Slide the track */
      if (this._track) {
        this._track.style.transform = `translateX(-${index * 100}%)`;
      }

      /* Update thumbs */
      this._thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('csk-banner__thumb--active', i === index);
      });

      /* Scroll active thumb into view — horizontal only, no page scroll */
      const activeThumb = this._thumbs[index];
      if (activeThumb) {
        const container = activeThumb.closest('[data-csk-thumbs]') || activeThumb.parentElement;
        if (container) {
          const thumbLeft = activeThumb.offsetLeft;
          const thumbWidth = activeThumb.offsetWidth;
          const containerWidth = container.offsetWidth;
          const scrollTarget = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);
          container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
        }
      }
    }

    _next() {
      const next = (this._current + 1) % this._slideCount;
      this._goTo(next);
    }

    _prev() {
      const prev = (this._current - 1 + this._slideCount) % this._slideCount;
      this._goTo(prev);
    }

    _startAutoplay() {
      this._clearTimeout();
      this._animateProgressBar();

      this._timeout = setTimeout(() => {
        this._next();
        if (!this._isPaused) this._startAutoplay();
      }, this._interval);
    }

    _pause() {
      this._isPaused = true;
      this._clearTimeout();
      this._stopProgressBar();
    }

    _animateProgressBar() {
      if (!this._progressBar) return;
      this._progressBar.style.transitionDuration = '0s';
      this._progressBar.style.width = '0%';

      void this._progressBar.offsetWidth;

      this._progressBar.style.transitionDuration = `${this._interval}ms`;
      this._progressBar.style.transitionTimingFunction = 'linear';
      this._progressBar.style.transitionProperty = 'width';
      this._progressBar.style.width = '100%';
    }

    _stopProgressBar() {
      if (!this._progressBar) return;
      const current = getComputedStyle(this._progressBar).width;
      this._progressBar.style.transitionDuration = '0s';
      this._progressBar.style.width = current;
    }

    _clearTimeout() {
      if (this._timeout) {
        clearTimeout(this._timeout);
        this._timeout = null;
      }
    }
  }

  customElements.define('csk-banner', CskBanner);
}
